import styled from 'styled-components';
import { ChangeEventHandler, FocusEventHandler, ReactNode } from 'react';
import typographyGenerator from 'typography/generator';

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;    
    margin: 5px 0px;
    border-color: ${(props => props.theme?.fonts?.label.color)};
    width: 100%;
    position: relative;
`;

const StyledSelect = styled.select<{error: any | undefined}>`
    ${(props => typographyGenerator('body', props.theme))}
    border: 2px solid transparent;
    height: 28px;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 10px 16px;
    width: 100%;
    min-width: 279px;
    height: 56px;
    background: ${(props => props.theme?.colors.LM_N20)};
    border-radius: 8px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    color: ${(props => props.theme?.colors.LM_N100)};
    transition: background-color 300ms ease-out;
    :focus{
        border: 2px solid ${(props => props.theme?.colors.BB100)};
    }
    :invalid {
        border: 2px solid ${(props => props.theme?.colors.PI100)};
    }
    ${(props) => props.error && `
        border: 2px solid ${props.theme?.colors.PI100};
    `};
    :hover {
        transition: background-color 300ms ease-out;
        background-color: ${(props => props.theme?.colors.LM_N30)};
    }
    background-image: url('/icons/chevron-down.svg');
    background-repeat: no-repeat, repeat;
    background-position: right .7em top 50%, 0 0;
    appearance: none;
    @media screen and (max-width: 375px) {
        min-width: 180px;
    }
`;

const Label = styled.div`
    ${(props => typographyGenerator('label', props.theme))}
    color: ${(props => props.theme?.colors.LM_N60)};
`;

interface IFormSelect {
    label: string,
    name: string | undefined,
    options: ReadonlyArray<string>;
    error?: string,
    errorIcon?: ReactNode | undefined,
    description?: string,
    handleChange: ChangeEventHandler<any> | undefined,
    handleBlur: FocusEventHandler<any> | undefined,
    value: string | ReadonlyArray<string> | number | undefined;
}

const DescriptionLabel = styled.label`
    ${(props => typographyGenerator('label', props.theme))}
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    line-height: 22px;
    color: ${(props => props.theme?.colors.LM_N60)}
`;

const ErrorIcon = styled.div`
    display: flex;
    flex-direction: row;
    position: absolute;
    right: 35px;
    height: 100%;
    align-items: center;
    color: ${(props => props.theme?.colors.RE100)};
`;

const ErrorLabel = styled.label`
    ${(props => typographyGenerator('label', props.theme))}
    padding: 2px 0px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    line-height: 22px;
    color: ${(props => props.theme?.colors.RE100)};
`;

const FormSelect: React.FC<IFormSelect> = ({ label, name, options, handleChange, handleBlur, value, description, error, errorIcon, }) => {
    return (
        <Container>
            <Label>{label}</Label>
            <InputContainer>
              <StyledSelect value={value} onChange={handleChange} name={name} onBlur={handleBlur} error={error}>
                {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </StyledSelect>
              {error && errorIcon ? <ErrorIcon>{errorIcon}</ErrorIcon> : null}
            </InputContainer>
            {error ? <ErrorLabel>{error}</ErrorLabel> : null}
            {description ? <DescriptionLabel>{description}</DescriptionLabel> : null}
        </Container>
    );
}

export default FormSelect
